/**
 * KuCoin Exchange API Service
 *
 * This service connects to the KuCoin Exchange API
 * with HMAC SHA-256 authentication + Passphrase (3rd parameter)
 *
 * API Docs: https://docs.kucoin.com
 */

import type {
  KuCoinCredentials,
  TradingBot,
  EarnPosition,
  EarnProduct,
  VIPTier,
  KuCoinStats,
  VIP_TIERS,
} from '../../types/kucoin';

interface APIResponse<T> {
  code: string; // "200000" for success
  msg?: string;
  data?: T;
}

class KuCoinAPI {
  private baseURL = 'https://api.kucoin.com';
  private credentials: KuCoinCredentials | null = null;
  private demoMode: boolean = false;

  /**
   * Set API credentials
   */
  setCredentials(credentials: KuCoinCredentials) {
    this.credentials = credentials;
    this.demoMode = false;
  }

  /**
   * Clear API credentials
   */
  clearCredentials() {
    this.credentials = null;
    this.demoMode = false;
  }

  /**
   * Check if credentials are set
   */
  hasCredentials(): boolean {
    return this.credentials !== null;
  }

  /**
   * Enable demo mode with mock data
   */
  setDemoMode(enabled: boolean) {
    this.demoMode = enabled;
  }

  /**
   * Check if in demo mode
   */
  isDemoMode(): boolean {
    return this.demoMode;
  }

  /**
   * Generate HMAC SHA-256 signature for KuCoin API
   *
   * Signature = HMAC-SHA256(timestamp + method + endpoint + body, apiSecret)
   */
  private async generateSignature(
    timestamp: number,
    method: 'GET' | 'POST' | 'DELETE',
    endpoint: string,
    body: string = ''
  ): Promise<string> {
    if (!this.credentials) {
      throw new Error('Credentials not set');
    }

    // Sign payload: timestamp + method + endpoint + body
    const signPayload = `${timestamp}${method}${endpoint}${body}`;

    // Browser: Web Crypto API
    const encoder = new TextEncoder();
    const keyData = encoder.encode(this.credentials.apiSecret);
    const messageData = encoder.encode(signPayload);

    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);

    // Convert to base64
    const signatureBase64 = btoa(
      String.fromCharCode(...new Uint8Array(signature))
    );

    return signatureBase64;
  }

  /**
   * Generate passphrase signature
   *
   * KC-API-PASSPHRASE = base64(HMAC-SHA256(passphrase, apiSecret))
   */
  private async generatePassphraseSignature(): Promise<string> {
    if (!this.credentials) {
      throw new Error('Credentials not set');
    }

    const encoder = new TextEncoder();
    const keyData = encoder.encode(this.credentials.apiSecret);
    const messageData = encoder.encode(this.credentials.passphrase);

    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);

    // Convert to base64
    const signatureBase64 = btoa(
      String.fromCharCode(...new Uint8Array(signature))
    );

    return signatureBase64;
  }

  /**
   * Make authenticated request to KuCoin API via Vercel proxy
   */
  private async makeRequest<T>(
    method: 'GET' | 'POST' | 'DELETE',
    endpoint: string,
    params: Record<string, any> = {}
  ): Promise<T> {
    if (!this.credentials) {
      throw new Error('Credentials not set. Call setCredentials() first.');
    }

    // Use Vercel serverless function as proxy to avoid CORS
    const proxyURL = '/api/kucoin/proxy';

    const response = await fetch(proxyURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        apiKey: this.credentials.apiKey,
        apiSecret: this.credentials.apiSecret,
        passphrase: this.credentials.passphrase,
        method,
        endpoint,
        params,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data: APIResponse<T> = await response.json();

    // KuCoin returns code "200000" for success
    if (data.code !== '200000') {
      throw new Error(data.msg || `API error: ${data.code}`);
    }

    return data.data as T;
  }

  /**
   * Fetch Trading Bots
   *
   * ⚠️ CUSTOM IMPLEMENTATION: KuCoin doesn't have native bot endpoint
   * Strategy:
   * 1. Fetch all orders via /api/v1/orders
   * 2. Filter orders with clientOid starting with "bot-"
   * 3. Group by bot ID
   * 4. Calculate stats (profit, trades, win rate)
   */
  async fetchTradingBots(): Promise<TradingBot[]> {
    if (this.demoMode) {
      return this.getMockTradingBots();
    }

    // TODO: Implement real API call when backend bot tracking is ready
    // For now, return mock data
    return this.getMockTradingBots();
  }

  /**
   * Fetch KuCoin Earn Positions
   */
  async fetchEarnPositions(): Promise<EarnPosition[]> {
    if (this.demoMode) {
      return this.getMockEarnPositions();
    }

    try {
      // GET /api/v1/earn/orders?status=ACTIVE
      const response = await this.makeRequest<any>(
        'GET',
        '/api/v1/earn/orders',
        { status: 'ACTIVE' }
      );

      if (!response.items || !Array.isArray(response.items)) {
        return [];
      }

      return response.items.map((item: any) => ({
        id: item.orderId,
        type: this.mapEarnType(item.type),
        currency: item.currency,
        amount: parseFloat(item.amount),
        apr: parseFloat(item.apr) * 100, // Convert to percentage
        apy: this.calculateAPY(parseFloat(item.apr) * 100),
        startDate: item.createdAt,
        maturityDate: item.maturityTime,
        totalRewards: parseFloat(item.income || '0'),
        pendingRewards: parseFloat(item.pendingIncome || '0'),
        status: item.status,
        lockupDays: item.period,
        autoRenew: item.autoRenew,
      }));
    } catch (error) {
      console.error('Error fetching Earn positions:', error);
      return this.getMockEarnPositions();
    }
  }

  /**
   * Fetch KuCoin Earn Products (available)
   */
  async fetchEarnProducts(): Promise<EarnProduct[]> {
    if (this.demoMode) {
      return this.getMockEarnProducts();
    }

    try {
      // GET /api/v1/earn/products
      const response = await this.makeRequest<any>('GET', '/api/v1/earn/products');

      if (!response.items || !Array.isArray(response.items)) {
        return [];
      }

      return response.items.map((item: any) => ({
        currency: item.currency,
        type: this.mapEarnType(item.type),
        minAmount: parseFloat(item.minPurchaseAmount),
        apr: parseFloat(item.interestRate) * 100,
        lockupDays: item.period,
        available: item.status === 'ONGOING',
        quota: parseFloat(item.quota || '0'),
      }));
    } catch (error) {
      console.error('Error fetching Earn products:', error);
      return this.getMockEarnProducts();
    }
  }

  /**
   * Fetch User VIP Tier Info
   */
  async fetchVIPTier(): Promise<VIPTier> {
    if (this.demoMode) {
      return this.getMockVIPTier();
    }

    try {
      // GET /api/v1/user-info
      const userInfo = await this.makeRequest<any>('GET', '/api/v1/user-info');

      const vipLevel = userInfo.vipLevel || 0;
      const tradingVolume30d = parseFloat(userInfo.volume30d || '0');

      const currentTier = VIP_TIERS.find((t) => t.level === vipLevel) || VIP_TIERS[0];
      const nextTierData = VIP_TIERS.find((t) => t.level === vipLevel + 1);

      return {
        level: vipLevel,
        tradingVolume30d,
        requiredVolume: currentTier.volume,
        makerFee: parseFloat(userInfo.makerFeeRate || currentTier.makerFee.toString()),
        takerFee: parseFloat(userInfo.takerFeeRate || currentTier.takerFee.toString()),
        nextTier: nextTierData
          ? {
              level: nextTierData.level,
              requiredVolume: nextTierData.volume,
              makerFee: nextTierData.makerFee,
              takerFee: nextTierData.takerFee,
              volumeNeeded: nextTierData.volume - tradingVolume30d,
            }
          : undefined,
      };
    } catch (error) {
      console.error('Error fetching VIP tier:', error);
      return this.getMockVIPTier();
    }
  }

  /**
   * Fetch Account Summary (KPIs)
   */
  async fetchAccountSummary(): Promise<KuCoinStats> {
    if (this.demoMode) {
      return this.getMockAccountSummary();
    }

    try {
      // Parallel fetch multiple endpoints
      const [accounts, earnPositions, vipTier] = await Promise.all([
        this.makeRequest<any>('GET', '/api/v1/accounts'),
        this.fetchEarnPositions(),
        this.fetchVIPTier(),
      ]);

      // Calculate totals
      const totalSpot = accounts.reduce(
        (sum: number, acc: any) =>
          acc.type === 'trade' ? sum + parseFloat(acc.balance) : sum,
        0
      );

      const totalEarn = earnPositions.reduce((sum, pos) => sum + pos.amount, 0);

      return {
        totalEquity: totalSpot + totalEarn,
        totalSpot,
        totalEarn,
        totalMargin: 0, // TODO: Fetch margin accounts
        totalFutures: 0, // TODO: Fetch futures positions
        totalRewards: earnPositions.reduce((sum, pos) => sum + pos.totalRewards, 0),
        earnPositions: earnPositions.length,
        activeBots: 0, // TODO: Calculated in fetchTradingBots
        tradingVolume24h: 0, // TODO: Fetch from orders
        tradingFeePaid24h: 0, // TODO: Fetch from orders
        vipTier,
      };
    } catch (error) {
      console.error('Error fetching account summary:', error);
      return this.getMockAccountSummary();
    }
  }

  /**
   * Helper: Map KuCoin earn type to our type
   */
  private mapEarnType(type: string): 'FLEXIBLE' | 'FIXED' | 'HIGH_YIELD' {
    if (type.toLowerCase().includes('flexible')) return 'FLEXIBLE';
    if (type.toLowerCase().includes('fixed')) return 'FIXED';
    return 'HIGH_YIELD';
  }

  /**
   * Helper: Calculate APY from APR
   * APY = (1 + APR/365)^365 - 1
   */
  private calculateAPY(apr: number): number {
    return (Math.pow(1 + apr / 365 / 100, 365) - 1) * 100;
  }

  /**
   * Mock Data: Trading Bots
   */
  private getMockTradingBots(): TradingBot[] {
    return [
      {
        id: 'bot-001',
        type: 'SPOT_GRID',
        symbol: 'BTC-USDT',
        status: 'ACTIVE',
        createdAt: Date.now() - 30 * 24 * 60 * 60 * 1000,
        updatedAt: Date.now(),
        config: {
          upperPrice: 50000,
          lowerPrice: 40000,
          gridLevels: 20,
          investmentPerGrid: 100,
        },
        stats: {
          profit: 1250.5,
          profitPercentage: 12.5,
          trades: 145,
          totalVolume: 58000,
          averageGridProfit: 8.62,
          winRate: 78.5,
        },
        orderIds: [],
        lastSyncedAt: Date.now(),
      },
      {
        id: 'bot-002',
        type: 'DCA',
        symbol: 'ETH-USDT',
        status: 'ACTIVE',
        createdAt: Date.now() - 15 * 24 * 60 * 60 * 1000,
        updatedAt: Date.now(),
        config: {
          upperPrice: 3500,
          lowerPrice: 2800,
          gridLevels: 10,
          investmentPerGrid: 200,
        },
        stats: {
          profit: 450.25,
          profitPercentage: 8.5,
          trades: 68,
          totalVolume: 22000,
          averageGridProfit: 6.62,
          winRate: 72.1,
        },
        orderIds: [],
        lastSyncedAt: Date.now(),
      },
      {
        id: 'bot-003',
        type: 'SPOT_GRID',
        symbol: 'SOL-USDT',
        status: 'STOPPED',
        createdAt: Date.now() - 45 * 24 * 60 * 60 * 1000,
        updatedAt: Date.now() - 5 * 24 * 60 * 60 * 1000,
        config: {
          upperPrice: 180,
          lowerPrice: 120,
          gridLevels: 15,
          investmentPerGrid: 50,
        },
        stats: {
          profit: -120.5,
          profitPercentage: -2.5,
          trades: 92,
          totalVolume: 12000,
          averageGridProfit: -1.31,
          winRate: 45.6,
        },
        orderIds: [],
        lastSyncedAt: Date.now() - 5 * 24 * 60 * 60 * 1000,
      },
    ];
  }

  /**
   * Mock Data: Earn Positions
   */
  private getMockEarnPositions(): EarnPosition[] {
    return [
      {
        id: 'earn-001',
        type: 'FLEXIBLE',
        currency: 'USDT',
        amount: 5000,
        apr: 8.5,
        apy: 8.86,
        startDate: Date.now() - 60 * 24 * 60 * 60 * 1000,
        totalRewards: 70.42,
        pendingRewards: 1.17,
        status: 'ACTIVE',
        autoRenew: true,
      },
      {
        id: 'earn-002',
        type: 'FIXED',
        currency: 'BTC',
        amount: 0.5,
        apr: 5.2,
        apy: 5.33,
        startDate: Date.now() - 20 * 24 * 60 * 60 * 1000,
        maturityDate: Date.now() + 10 * 24 * 60 * 60 * 1000,
        totalRewards: 0.00142,
        pendingRewards: 0.00007,
        status: 'ACTIVE',
        lockupDays: 30,
        autoRenew: false,
      },
      {
        id: 'earn-003',
        type: 'HIGH_YIELD',
        currency: 'ETH',
        amount: 2.5,
        apr: 12.8,
        apy: 13.65,
        startDate: Date.now() - 10 * 24 * 60 * 60 * 1000,
        maturityDate: Date.now() + 80 * 24 * 60 * 60 * 1000,
        totalRewards: 0.0088,
        pendingRewards: 0.0009,
        status: 'ACTIVE',
        lockupDays: 90,
        autoRenew: true,
      },
    ];
  }

  /**
   * Mock Data: Earn Products
   */
  private getMockEarnProducts(): EarnProduct[] {
    return [
      {
        currency: 'USDT',
        type: 'FLEXIBLE',
        minAmount: 100,
        apr: 8.5,
        available: true,
        quota: 1000000,
      },
      {
        currency: 'BTC',
        type: 'FIXED',
        minAmount: 0.01,
        apr: 5.2,
        lockupDays: 30,
        available: true,
        quota: 50,
      },
      {
        currency: 'ETH',
        type: 'HIGH_YIELD',
        minAmount: 0.1,
        apr: 12.8,
        lockupDays: 90,
        available: true,
        quota: 500,
      },
    ];
  }

  /**
   * Mock Data: VIP Tier
   */
  private getMockVIPTier(): VIPTier {
    return {
      level: 2,
      tradingVolume30d: 250000,
      requiredVolume: 200000,
      makerFee: 0.06,
      takerFee: 0.1,
      nextTier: {
        level: 3,
        requiredVolume: 500000,
        makerFee: 0.04,
        takerFee: 0.09,
        volumeNeeded: 250000,
      },
    };
  }

  /**
   * Mock Data: Account Summary
   */
  private getMockAccountSummary(): KuCoinStats {
    const earnPositions = this.getMockEarnPositions();
    const bots = this.getMockTradingBots();

    return {
      totalEquity: 25450.75,
      totalSpot: 18200.5,
      totalEarn: 7250.25,
      totalMargin: 0,
      totalFutures: 0,
      totalRewards: earnPositions.reduce((sum, pos) => sum + pos.totalRewards, 0),
      earnPositions: earnPositions.length,
      activeBots: bots.filter((b) => b.status === 'ACTIVE').length,
      tradingVolume24h: 12500,
      tradingFeePaid24h: 12.5,
      vipTier: this.getMockVIPTier(),
    };
  }
}

export const kucoinAPI = new KuCoinAPI();

// Re-export types for convenience
export type {
  KuCoinCredentials,
  TradingBot,
  EarnPosition,
  EarnProduct,
  VIPTier,
  KuCoinStats,
};
